import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'

export const useModalStore = defineStore('modalManager', () => {

  const focusAfterClose = ref('') //id to focus when modal is closed

  // Confirmation dialog state
  const confirmationDialog = reactive({
    visible: false,
    title: '',
    text: '',
    maxWidth: 400,
    confirmText: '',
    cancelText: '',
    confirmColor: '',
    resolve: null,
  })

  // Edit field dialog state
  const inputDialog = reactive({
    isOpen: false,
    title: '',
    label: '',
    placeholder: '',
    confirmActionTitle: '',
    value: '',
    width: 400,
    isLoading: false,
    resolve: null,
    reject: null,
    validation: null,
    maxLength: null,
    onSave: null,
  })

  // Modal states
  //TODO: naming convention - no "Dialog" suffixes, the rest as the component is named
  const modals = reactive({
    calendarEvent: false, // Główne okno edycji wizyty
    calendarSales: false, // Sprzedaz produktów
    addNewCustomer: false, // Dodawanie nowego klienta
    customerCardDialog: false, // Karta klienta
    sendSmsToCustomer: false,
    selectEmployee: false, // Wybór pracownika wykonujacego pojedyncza usługę
    selectEmployeeFinalizationView: false, // Wybór pracownika wykonujacego pojedyncza usługę w widoku finalizacji
    editTimeAndPrice: false, // Edycja czasu i ceny pojedynczej usługi
    suggestNewDateAndTime: false, // Proponowany termin
    visitChangesDialog: false, // Historia zmian w wizycie
    cancelEvent: false, // Anulowanie wizyty
    reserveList: false,
    addReserveListItem: false, // Edycja/tworzenie wpisy do listy rezerwowej
    editCurrentService: false,
    editProduct: false, // Edycja kwot produktów w widoku finalizacji
    cashRegister: false, // Edycja kwot w modalu kasy
    selectSeller: false, // Edycja sprzedawcy produktu w widoku finalizacji
    cyclicVisitUpdate: false, // Potwierdź zmiany w cyklicznej wizycie
    cycleVisitDialog: false, // Wizyty cykliczne
    searchBoxDialog: false, // Wyszukiwarka
    overrideCyclicVisitDialog: false, // Nadpisz pojedynczą/wszystkie wizyty w cyklu
    deleteCyclicVisitDialog: false, // Usuń pojedyńczą/wszystkie wizyty w cyklu
    editDate: false, // Generyczna edycja daty
    addSupplier: false, // Add new supplier modal
    editSupplier: false, // Edit supplier modal
    editStorageProduct: false, // Edycja produktu magazynowego
    storageProductHistory: false, // Historia produktu magazynowego
    sendOrder: false, // Wysyłka zamówienia do dostawcy na e-mail
    orderEmailHistory: false, // Historia wysyłki zamówienia na e-mail
    soldProductsPreview: false, // Podgląd sprzedanych produktów (kalendarz -> ikona "..."
    wealthonTransactionResult: false, // Potwierdzenie wyniku transakcji Wealthon
    tagsManager: false, // Zarządzanie tagami (lista)
    editTag: false, // Dodawanie/edycja tagu
  })

  // Store for modal parameters
  const modalParams = reactive({})
  const modalResults = reactive({})
  const modalResolvers = reactive({})

  //
  // Manage dialogs
  // modalName -
  // focusElementId - id of html element that will be focused after modal is closed;
  // params - object with parameters to pass to modal
  //
  const openModal = (modalName, focusElementId = '', closeOtherModals = false, params = {}) => {
    if (closeOtherModals) {
      resetModals()
    }
    if (modals[modalName] !== undefined) {
      modals[modalName] = true
      // Set parameters for the modal
      modalParams[modalName] = params
      // Create new Promise for this modal instance
      return new Promise((resolve) => {
        modalResolvers[modalName] = resolve
      })
    } else {
      console.error(`Modal '${modalName}' does not exists.`)
      return Promise.reject(new Error(`Modal '${modalName}' nie istnieje.`))
    }
    if (focusElementId.length) {
      focusAfterClose.value = focusElementId
    }
  }

  const closeModal = (modalName, result = null) => {
    if (modals[modalName] !== undefined) {
      modals[modalName] = false
      // Store the result
      modalResults[modalName] = result
      // Resolve the promise if there's a resolver
      if (modalResolvers[modalName]) {
        modalResolvers[modalName](result)
        modalResolvers[modalName] = null
      }
      // Clear parameters when closing modal
      modalParams[modalName] = {}
    } else {
      console.error(`Modal '${modalName}' nie istnieje.`)
    }
  }

  const toggleModal = (modalName) => {
    if (modals[modalName] !== undefined) {
      modals[modalName] = !modals[modalName]
    } else {
      console.error(`Modal '${modalName}' nie istnieje.`)
    }
  }

  const resetModals = () => {
    Object.keys(modals).forEach((key) => {
      modals[key] = false
      modalParams[key] = {}
      modalResults[key] = null
      if (modalResolvers[key]) {
        modalResolvers[key](null)
        modalResolvers[key] = null
      }
    })
  }

  // Opens confirmation modal with a message and title
  const openConfirmationModal = (message, title = '', options = {}) => {
    return new Promise((resolve) => {
      confirmationDialog.title = title
      confirmationDialog.text = message
      confirmationDialog.maxWidth = options.maxWidth ?? 400
      confirmationDialog.confirmText = options.confirmText ?? ''
      confirmationDialog.cancelText = options.cancelText ?? ''
      confirmationDialog.confirmColor = options.confirmColor ?? ''
      confirmationDialog.visible = true
      confirmationDialog.resolve = resolve
    })
  }

  // Handles the confirmation modal result
  const handleConfirmation = (result) => {
    if (confirmationDialog.resolve) {
      confirmationDialog.resolve(result)
      confirmationDialog.resolve = null
    }
    confirmationDialog.visible = false
    setTimeout(() => {
      confirmationDialog.title = ''
      confirmationDialog.text = ''
      confirmationDialog.confirmText = ''
      confirmationDialog.cancelText = ''
      confirmationDialog.confirmColor = ''
    }, 250)
  }

  // ========================================
  // Edit dialog for single field
  // ========================================
  const openInputModal = (
    title,
    label,
    placeholder,
    confirmActionTitle,
    initialValue = '',
    width = 400,
    validation = null,
    maxLength = null,
    onSave = null,
  ) => {
    return new Promise((resolve) => {
      inputDialog.title = title
      inputDialog.label = label
      inputDialog.placeholder = placeholder
      inputDialog.confirmActionTitle = confirmActionTitle
      inputDialog.value = initialValue
      inputDialog.width = width
      inputDialog.validation = validation
      inputDialog.maxLength = maxLength
      inputDialog.isOpen = true
      inputDialog.isLoading = false
      inputDialog.onSave = onSave
      inputDialog.resolve = resolve
    })
  }

  const closeInputModal = (saved) => {
    if (inputDialog.resolve && !saved) {
      inputDialog.resolve(null)
    }

    inputDialog.isOpen = false

    setTimeout(() => {
      inputDialog.title = ''
      inputDialog.label = ''
      inputDialog.placeholder = ''
      inputDialog.confirmActionTitle = ''
      inputDialog.value = ''
      inputDialog.validation = null
      inputDialog.maxLength = null
      inputDialog.resolve = null
      inputDialog.onSave = null
    }, 300)
  }

  const setInputModalLoadingState = (loading) => {
    inputDialog.isLoading = loading
  }


  return {
    modals,
    modalParams,
    modalResults,
    openModal,
    closeModal,
    toggleModal,
    resetModals,
    focusAfterClose,
    confirmationDialog,
    openConfirmationModal,
    handleConfirmation,
    inputDialog,
    openInputModal,
    closeInputModal,
    setInputModalLoadingState,
  }
})
